import * as React from 'react';
import ReactCalendar, { ViewCallbackProperties } from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { RouteComponentProps } from 'react-router-dom';

import * as isEqual from 'date-fns/isEqual';
import * as addYears from 'date-fns/addYears';
import * as addDays from 'date-fns/addDays';
import * as format from 'date-fns/format';
import * as isAfter from 'date-fns/isAfter';
import * as isSameDay from 'date-fns/isSameDay';
import * as startOfDay from 'date-fns/startOfDay';
import * as getDay from 'date-fns/getDay';
import * as isWithinInterval from 'date-fns/isWithinInterval';
import * as toDate from 'date-fns/toDate';
import * as isBefore from 'date-fns/isBefore';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import * as classNames from 'classnames';
import DelayedSpinner from 'react/presentation/core/DelayedSpinner';
import LeftChevronIcon from 'react/presentation/core/icons/ChevronLeft';
import RightChevronIcon from 'react/presentation/core/icons/ChevronRight';

const IFRAME_EVENT_OPEN_IFRAME_FROM_EMBED_CLALENDAR = 'recreogo.openIframeFromEmbedCalendar';
const DAYS_AMOUNT_DIFFERENCE_FOR_UNAVAILABLE_DATES = 60;

interface ICalendarSeason {
  id: number;
  seasonStart: Date;
  seasonEnd: Date;
  hourSet?: { [key: string]: string };
  blackoutDates?: Array<{ startDate: Date; endDate: Date }>;
}

interface ICalendarRate {
  hidden?: boolean;
  seasonRates?: Array<{ season: ICalendarSeason }>;
}

interface ICalendarListing {
  id: number;
  rates: ICalendarRate[];
  seasons: ICalendarSeason[];
}

interface IListingIframeCalenarRouteProps {
  listingId: string;
}

interface IListingIframeCalenarProps extends Partial<RouteComponentProps<IListingIframeCalenarRouteProps>> {
  listingId?: string;
}
interface IPostMessage {
  type: string;
  data?: any;
}

const notifyParentWindowFromIframe = (type: string, data?: any) => {
  const messageObject: IPostMessage = { type };
  if (data) {
    messageObject.data = data;
  }
  window.parent.postMessage(JSON.stringify(messageObject), '*');
};

const getQueryParameter = (name: string) => {
  const regex = new RegExp(`[?&]${name.replace(/[\[\]]/g, '\\$&')}(=([^&#]*)|&|#|$)`);
  const results = regex.exec(window.location.href);
  if (!results) {
    return null;
  }
  if (!results[2]) {
    return '';
  }
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
};

const toDateFromQuery = (date: string | Date) => {
  if (!date) {
    return new Date();
  }
  if (date instanceof Date) {
    return date;
  }
  if (date.toLowerCase() === 'today') {
    return new Date();
  }
  if (date.toLowerCase() === 'tomorrow') {
    return addDays(new Date(), 1);
  }
  return toDate(date);
};

const clearTimeComponent = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const setTimeComponentAsMax = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
const toLocaleISODate = (date: Date) => (date instanceof Date ? format(date, 'yyyy-MM-dd') : date);

const getEndUnavailableCalendarDate = (startDate: Date, view: ViewCallbackProperties['view'], customDaysToLoad?: number) => {
  let activeStartDate = startDate;

  if (isAfter(startOfDay(new Date()), startOfDay(activeStartDate))) {
    activeStartDate = new Date();
  }

  if (view === 'year') {
    return addYears(activeStartDate, 1);
  }

  return addDays(activeStartDate, customDaysToLoad || 30);
};

const shouldGetUnvailableDates = (prevDate: Date, newDate: Date, calendarClickProps: ViewCallbackProperties, customAmountOfDays?: number): boolean => {
  if (
    calendarClickProps.view === 'month' &&
    calendarClickProps.action === 'next' &&
    isAfter(prevDate, addDays(calendarClickProps.activeStartDate, customAmountOfDays || DAYS_AMOUNT_DIFFERENCE_FOR_UNAVAILABLE_DATES))
  ) {
    return false;
  }
  return !isSameDay(prevDate, newDate) && isAfter(newDate, prevDate);
};

const hasHoursForDay = (seasonHours: ICalendarSeason['hourSet'], date: Date) => {
  if (!seasonHours) {
    return false;
  }

  if (getDay(date) === 0) return !!(seasonHours.openTimeSunday && seasonHours.closeTimeSunday);
  if (getDay(date) === 1) return !!(seasonHours.openTimeMonday && seasonHours.closeTimeMonday);
  if (getDay(date) === 2) return !!(seasonHours.openTimeTuesday && seasonHours.closeTimeTuesday);
  if (getDay(date) === 3) return !!(seasonHours.openTimeWednesday && seasonHours.closeTimeWednesday);
  if (getDay(date) === 4) return !!(seasonHours.openTimeThursday && seasonHours.closeTimeThursday);
  if (getDay(date) === 5) return !!(seasonHours.openTimeFriday && seasonHours.closeTimeFriday);
  if (getDay(date) === 6) return !!(seasonHours.openTimeSaturday && seasonHours.closeTimeSaturday);

  return false;
};

const normalizeSeason = (season: ICalendarSeason) => ({
  ...season,
  seasonStart: toDate(season.seasonStart),
  seasonEnd: toDate(season.seasonEnd),
  blackoutDates: (season.blackoutDates || []).map((blackout) => ({
    ...blackout,
    startDate: toDate(blackout.startDate),
    endDate: toDate(blackout.endDate),
  })),
});

const isInBlackoutRange = (blackouts: ICalendarSeason['blackoutDates'], date: Date) => {
  return (blackouts || []).some((blackout) =>
    isWithinInterval(date, { start: clearTimeComponent(blackout.startDate), end: setTimeComponentAsMax(blackout.endDate) }),
  );
};

const getSeasonDates = (season: ICalendarSeason) => {
  const dates: Date[] = [];
  let currentDate = clearTimeComponent(season.seasonStart);

  while (currentDate <= season.seasonEnd) {
    if (hasHoursForDay(season.hourSet, currentDate) && !isInBlackoutRange(season.blackoutDates, currentDate)) {
      dates.push(currentDate);
    }
    currentDate = addDays(currentDate, 1);
  }

  return dates;
};

const getApiUrl = (v2: boolean = false) => {
  const globals = (window as any).Globals || {};
  return (v2 ? globals.ApiUrlV2 : globals.ApiUrl || '').replace(/\/$/, '');
};

const requestJson = async (url: string, init?: RequestInit) => {
  const response = await fetch(url, {
    ...init,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-GMT-Offset': (new Date().getTimezoneOffset() / 60.0).toString(),
      'X-TimeZone-Id': Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...(init && init.headers),
    },
  });

  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  return response.json();
};

const getListingById = async (listingId: string): Promise<ICalendarListing> => {
  const response = await requestJson(`${getApiUrl()}/listings/${listingId}`);
  const listing = response.value || response.Value || response;
  return {
    id: listing.id,
    rates: listing.rates || [],
    seasons: (listing.seasons || []).map(normalizeSeason),
  };
};

const getUnavailableDatesForListing = async (listingId: string, dateFrom: Date, dateTo: Date): Promise<Date[]> => {
  const response = await requestJson(`${getApiUrl(true)}/listings/unavailable-dates`, {
    method: 'POST',
    body: JSON.stringify({
      listingId: [+listingId],
      dateFrom: toLocaleISODate(dateFrom),
      dateTo: toLocaleISODate(dateTo),
    }),
  });

  return (response.dates || []).map((date: string) => new Date(date));
};

const ListingIframeCalenar: React.FC<IListingIframeCalenarProps> = (props) => {
  const listingId = (props.match && props.match.params.listingId) || props.listingId || '';

  let startDateFromQuery = toDateFromQuery(getQueryParameter('date'));

  if (isBefore(startDateFromQuery, new Date())) {
    startDateFromQuery = new Date();
  }

  const [isDataLoading, setIsDataLoading] = React.useState(false);
  const [isListingLoading, setListingLoading] = React.useState(false);
  const [listing, setListing] = React.useState<ICalendarListing>({ id: 0, rates: [], seasons: [] });

  const [availableDays, setAvailableDays] = React.useState<Date[]>([]);
  const [startCalendarDate, setStartCalendarDate] = React.useState(startDateFromQuery);
  const [endCalendarDate, setEndCalendarDate] = React.useState(getEndUnavailableCalendarDate(startDateFromQuery, 'month', 60));

  const [unavailableDatesLoading, setUnavailableDatesLoading] = React.useState(false);
  const [unavailableDates, setUnavailableDates] = React.useState<Date[]>([]);

  const [rentalStartDateTime, setRentalStartDateTime] = React.useState<Date>(startDateFromQuery);

  const globals = (window as any).Globals || {};

  const getUnavailableDates = async (concatValues: boolean = false, customStartDate?: Date, customEndDate?: Date) => {
    try {
      setUnavailableDatesLoading(true);
      const newStartDate = customStartDate || startCalendarDate;
      const newEndDate = customEndDate || endCalendarDate;
      const dates = await getUnavailableDatesForListing(listingId, newStartDate, newEndDate);
      const newUnavailableDates = concatValues ? Array.from(new Set([...unavailableDates, ...dates])) : dates;
      setUnavailableDates(newUnavailableDates);
      setUnavailableDatesLoading(false);
      return newUnavailableDates;
    } catch (err) {
      setUnavailableDatesLoading(false);
      // tslint:disable-next-line:no-console
      console.error(err);
    }
  };

  const isOverBookAvalable = () => {
    // hard coded in order to decrease future changes in case when booking on consumenr will allow overbooking
    return false;
  };

  const getSeasons = (customListing?: ICalendarListing) => {
    const listingToProcess = customListing || listing;
    const rates = isOverBookAvalable() ? listingToProcess.rates : listingToProcess.rates.filter((r) => !r.hidden);
    const seasonsById = new Map<number, ICalendarSeason>();
    const rateSeasons = rates.reduce((seasons: ICalendarSeason[], rate) => {
      return seasons.concat((rate.seasonRates || []).map((seasonRate) => seasonRate.season));
    }, []);

    listingToProcess.seasons.concat(rateSeasons).forEach((season) => {
      if (season) {
        seasonsById.set(season.id, normalizeSeason(season));
      }
    });

    return Array.from(seasonsById.values());
  };

  const recalculateAvailableDays = async (customUnavailableDates?: Date[], customListing?: ICalendarListing) => {
    try {
      const unavailableDatesToProcess = customUnavailableDates || unavailableDates;
      const listingToProcess = customListing || listing;
      const overBookAvailable = isOverBookAvalable();
      const seasons = getSeasons(listingToProcess);
      const today = new Date();
      const yesterday = new Date(today.setDate(today.getDate() - 1));

      let newAvailableDays: any[] = [];
      if (seasons) {
        newAvailableDays = seasons
          .reduce((dates, season) => dates.concat(getSeasonDates(season)), [])
          .filter((date) => isAfter(clearTimeComponent(date), clearTimeComponent(yesterday)));
        if (!overBookAvailable) {
          newAvailableDays = newAvailableDays.filter((date) => unavailableDatesToProcess.findIndex((d) => isEqual(date, d)) === -1);
        }

        setAvailableDays(newAvailableDays);
      }
    } catch (err) {
      // tslint:disable-next-line:no-console
      console.error(err);
    }
  };

  const getListing = async () => {
    try {
      setListingLoading(true);
      const newListing = await getListingById(listingId);

      setListing(newListing);
      setListingLoading(false);
      return newListing;
    } catch (err) {
      setListingLoading(false);
      // tslint:disable-next-line:no-console
      console.error(err);
    }
  };

  React.useEffect(() => {
    const loadData = async () => {
      setIsDataLoading(true);
      const newListing = await getListing();
      const newUnavailableDates = await getUnavailableDates();
      await recalculateAvailableDays(newUnavailableDates, newListing);
      setIsDataLoading(false);
    };
    loadData();
  }, [listingId]);

  const setRentalStartTime = async (startDate: Date) => {
    setRentalStartDateTime(startDate);
  };

  const onActiveStartDateChange = async (calendarClickProps: ViewCallbackProperties) => {
    const { action } = calendarClickProps;

    const yearDate = addYears(new Date(), 1);
    let newEndCalendarDate;
    let loadForFullYear = false;
    let concatUnavailableDates = false;

    if (action === 'drillUp') {
      // load for full year
      newEndCalendarDate = yearDate;
      loadForFullYear = true;
    }

    if (action === 'next') {
      // known behaviour - if user clicks multiple times to the next calender btn, then back, then again next - it will load dates for the  next date range
      newEndCalendarDate = getEndUnavailableCalendarDate(endCalendarDate, calendarClickProps.view, 60);
      concatUnavailableDates = true;
    }

    if (newEndCalendarDate && shouldGetUnvailableDates(endCalendarDate, newEndCalendarDate, calendarClickProps, 90)) {
      setEndCalendarDate(newEndCalendarDate);
      setStartCalendarDate(loadForFullYear ? new Date() : endCalendarDate);

      const newUnavailableDates = await getUnavailableDates(loadForFullYear || concatUnavailableDates, loadForFullYear ? new Date() : endCalendarDate, newEndCalendarDate);
      await recalculateAvailableDays(newUnavailableDates);
    }
  };

  const handleBookNowClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();
    event?.stopPropagation();

    const url = (globals.WebUrl || window.location.origin).replace(/\/$/, '');

    notifyParentWindowFromIframe(IFRAME_EVENT_OPEN_IFRAME_FROM_EMBED_CLALENDAR, {
      iframePath: `${url}/consumer/listing/${listing.id}/rent?date=${format(rentalStartDateTime, 'yyyy-MM-dd')}&version=2`,
    });
  };

  const checkTile = ({ date, view }: any) => {
    // check if particular tile with date range should be enabled
    switch (view) {
      case 'century': // in this case each tile has date value equal to max year of range plus 1
        return !availableDays.find((d) => d.getFullYear() >= date.getFullYear() && date.getFullYear() + 10 >= d.getFullYear());
      case 'decade':
        return !availableDays.find((d) => d.getFullYear() === date.getFullYear());
      case 'year':
        return !availableDays.find((d) => d.getFullYear() === date.getFullYear() && d.getMonth() === date.getMonth());
      case 'month':
      default:
        return !availableDays.find((d) => isEqual(d, date));
    }
  };

  return (
    <div className="recreogo-ListingIframeCalenar__container">
      <div style={{ clear: 'both' }} />

      <Typography className="recreogo-ListingIframeCalenar__title" variant="h5">
        Book online with real-time availability
      </Typography>
      <div style={{ position: 'relative' }}>
        {(isDataLoading || unavailableDatesLoading || isListingLoading) && <DelayedSpinner />}
        <ReactCalendar
          onChange={(date: Date | Date[]) => setRentalStartTime(date as Date)}
          value={rentalStartDateTime}
          calendarType="US"
          navigationLabel={({ label }) => (
            <>
              {label} <RightChevronIcon className="recreogo-react-calendar__navigation__label-arrow" />
            </>
          )}
          nextLabel={<RightChevronIcon />}
          prevLabel={<LeftChevronIcon />}
          tileDisabled={isDataLoading || unavailableDatesLoading || isListingLoading ? () => true : checkTile}
          onActiveStartDateChange={onActiveStartDateChange}
          minDetail="year"
          className={classNames({ 'react-calendar--loading': isDataLoading || unavailableDatesLoading || isListingLoading }, 'recreogo-ListingIframeCalenar__calendar')}
          defaultActiveStartDate={startDateFromQuery}
          minDate={startDateFromQuery}
        />
      </div>
      <div className="recreogo-ListingIframeCalenar__buttons">
        <Button
          className="recreogo-mui-button"
          variant="contained"
          color="secondary"
          onClick={handleBookNowClick}
          disabled={!rentalStartDateTime || isDataLoading || unavailableDatesLoading || isListingLoading}
          disableElevation
        >
          Book now
        </Button>
      </div>
    </div>
  );
};

export default ListingIframeCalenar;
